import { useState } from "react";
import { Shield, Globe, Hash, Sparkles, Tag, Cpu, Copy, Check } from "lucide-react";
import { COLORS } from "../styles/tokens";

interface MetadataViewProps {
  isMobile: boolean;
}

type SchemaTab = "signal" | "before" | "after" | "impression";

type ExampleKey =
  | "wx_storm" | "wx_aqi" | "fw_spread"
  | "sp_goal"  | "sp_golf"
  | "ws_wnba"  | "ws_nwsl"
  | "fi_10k"   | "fi_mna"
  | "gm_dota2"
  | "ent_starmeter";

interface ExampleMeta { label: string; filename: string }

const EXAMPLES: Record<ExampleKey, ExampleMeta> = {
  wx_storm:     { label: "storm_alert",   filename: "signal.wx_storm_alert.635.json"  },
  wx_aqi:       { label: "aqi_spike",     filename: "signal.wx_aqi_spike.501.json"    },
  fw_spread:    { label: "pred_spread",   filename: "signal.fw_pred_spread.ca.json"   },
  sp_goal:      { label: "ftb_red_card",  filename: "signal.sp_ftb_red.501.json"      },
  sp_golf:      { label: "golf_leader",   filename: "signal.sp_golf_lead.json"        },
  ws_wnba:      { label: "wnba_close",    filename: "signal.sp_wnba_close.501.json"    },
  ws_nwsl:      { label: "nwsl_goal",     filename: "signal.sp_nwsl_goal.820.json"     },
  fi_10k:       { label: "10k_filing",    filename: "signal.fi_10k_filing.json"       },
  fi_mna:       { label: "mna_active",    filename: "signal.fi_mna_active.json"       },
  gm_dota2:     { label: "dota2_ccu",     filename: "signal.gm_dota2_ccu.json"       },
  ent_starmeter:{ label: "starmeter",     filename: "signal.ent_starmeter.501.json"   },
};

interface ExampleGroup {
  label: string;
  color: string;
  keys: ExampleKey[];
}

const EXAMPLE_GROUPS: ExampleGroup[] = [
  { label: "WEATHER",  color: COLORS.cyan,   keys: ["wx_storm", "wx_aqi", "fw_spread"]   },
  { label: "SPORTS",   color: COLORS.amber,  keys: ["sp_goal", "sp_golf"]                },
  { label: "WOMEN'S",  color: COLORS.pink,   keys: ["ws_wnba", "ws_nwsl"]                },
  { label: "FINANCE",  color: COLORS.lime,   keys: ["fi_10k", "fi_mna"]                  },
  { label: "GAMING",   color: COLORS.violet, keys: ["gm_dota2"]                           },
  { label: "ENTMT",    color: COLORS.rose,   keys: ["ent_starmeter"]                      },
];

const SIGNAL_TAGS: Record<ExampleKey, object> = {
  wx_storm: {
    signal_id: "weather.RSQ_SevereStorm_Alert",
    package: "RSQ-WX",
    schema_version: "2026.1",
    emitted_at: "2026-05-19T14:22:07Z",
    ttl_seconds: 10800,
    geo: { country: "USA", region: "TN", dma: "635", zip: "38103" },
    intent_score: 0.91,
    activation: {
      deal_id: "RSQ-WX-STORM-ALERT",
      audience_kv: { kv_key: "rsy_sig", kv_val: "wx_storm_alert" },
    },
    creative_cue: "Storm conditions approaching — prepare your facility now.",
    schain_compatible: true,
  },
  wx_aqi: {
    signal_id: "weather.RSQ_AirQuality_Health",
    package: "RSQ-WX",
    schema_version: "2026.1",
    emitted_at: "2026-04-29T18:42:11Z",
    ttl_seconds: 7200,
    geo: { country: "USA", dma: "501", region: "NY" },
    aqi: 168, pollutant: "PM2.5",
    intent_score: 0.87,
    activation: {
      deal_id: "RSQ-WX-AQI-HEALTH",
      audience_kv: { kv_key: "rsy_sig", kv_val: "wx.aqi_spike.501" },
    },
    schain_compatible: true,
  },
  fw_spread: {
    signal_id: "firewatch.RSQ_FW_PredictiveSpread",
    package: "RSQ-FW",
    schema_version: "2026.1",
    emitted_at: "2026-05-19T09:14:22Z",
    ttl_seconds: 3600,
    geo: { country: "USA", region: "CA", polygon_id: "fw:polygon:ca:los-padres-nf" },
    forecast_perimeter_km2: 847,
    wind_mph: 28,
    rh_pct: 14,
    intent_score: 0.96,
    activation: {
      deal_id: "RSQ-FW-PRED-SPREAD",
      audience_kv: { kv_key: "rsy_sig", kv_val: "fw.pred_spread" },
    },
    creative_cue: "Wildfire spread forecast active — route impacts imminent.",
    schain_compatible: true,
  },
  sp_goal: {
    pkg: "RSQ-SP",
    sport: "soccer",
    competition: "EPL",
    match_id: "sp:football:12345",
    state: "live",
    clock_min: 67,
    event: { type: "red_card", team: "away", player_id: "sp:p:987", minute: 66 },
    geo_scope: "DMA:US:501",
    ttl_min: 50,
    deal_id: "FTB-LIVE-RED",
    schain_compatible: true,
  },
  sp_golf: {
    pkg: "RSQ-SP",
    sport: "golf",
    competition: "PGA-TOUR",
    round: 4,
    hole: 15,
    event: { type: "lead_change", new_leader: "sp:p:441", score: -14, margin: 1 },
    players_in_contention: 3,
    geo_scope: "NATIONAL:US",
    ttl_min: 40,
    deal_id: "GOLF-LIVE-LEADCHG",
    schain_compatible: true,
  },
  ws_wnba: {
    pkg: "RSQ-SP",
    sport: "basketball",
    league: "WNBA",
    competition: "WNBA",
    match_id: "sp:wnba:20260625:nyl-lva",
    state: "live",
    clock_min: 38,
    event: { type: "close_game", margin: 3, period: "Q4", home: "NYL", away: "LVA" },
    geo_scope: "DMA:US:501",
    ttl_min: 75,
    deal_id: "WNBA-LIVE-CL5",
    schain_compatible: true,
  },
  ws_nwsl: {
    pkg: "RSQ-SP",
    sport: "soccer",
    league: "NWSL",
    competition: "NWSL",
    match_id: "sp:nwsl:20260625:por-gfc",
    state: "live",
    clock_min: 58,
    event: { type: "goal", team: "home", player_id: "sp:p:5521", minute: 57 },
    geo_scope: "DMA:US:820",
    ttl_min: 25,
    deal_id: "NWSL-LIVE-GOAL",
    schain_compatible: true,
  },
  fi_10k: {
    pkg: "RSQ-FI",
    signal: "finance.RSQ_FIN_10K",
    ticker: "AAPL",
    exchange: "NASDAQ",
    filing_type: "10-K",
    filed_at: "2026-04-29T16:00:00Z",
    sentiment_score: 0.74,
    ttl_hours: 72,
    activation: {
      deal_id: "RSQ-FI-10K-AAPL",
      audience_kv: { kv_key: "rsy_sig", kv_val: "fi.10k.aapl" },
    },
    schain_compatible: true,
  },
  fi_mna: {
    signal_id: "finance.RSQ_FIN_MNA_ACTIVE",
    package: "RSQ-FI",
    schema_version: "2026.1",
    emitted_at: "2026-05-19T13:02:47Z",
    ttl_seconds: 7200,
    ticker_target: "XYZ",
    ticker_acquirer: "BIGCO",
    deal_value_bn: 4.2,
    exchange: "NYSE",
    geo: { country: "USA" },
    intent_score: 0.88,
    activation: {
      deal_id: "RSQ-FIN-MNA-ACTIVE",
      audience_kv: { kv_key: "rsy_sig", kv_val: "fi.mna_active" },
    },
    schain_compatible: true,
  },
  gm_dota2: {
    event_id: "steam.ccu:appid.570:2026-05-19T14:10Z",
    source: "steam",
    signal: "ccu_zscore",
    appid: 570,
    title: "Dota 2",
    z: 2.8,
    value: 558123,
    intent_score: 0.84,
    deal_ids: ["RSQ-GM-E", "RSQ-GM-F"],
    ttl_sec: 86400,
    schain_compatible: true,
  },
  ent_starmeter: {
    signal_id: "entertainment.RSQ_STARmeter_Top100_7D",
    package: "RSQ-ENT",
    schema_version: "2026.1",
    emitted_at: "2026-05-19T00:00:00Z",
    ttl_seconds: 604800,
    title_id: "imdb:tt23289160",
    title: "Andor (Season 2)",
    starmeter_rank: 7,
    starmeter_prev_rank: 41,
    platform: "Disney+",
    geo: { country: "USA", dma: "501" },
    intent_score: 0.82,
    activation: {
      deal_id: "RSQ_STARmeter_Top100_7D_DMA_501",
      audience_kv: { kv_key: "rsy_sig", kv_val: "ent.starmeter.top100.501" },
    },
    schain_compatible: true,
  },
};

const BID_GEO: Record<ExampleKey, object> = {
  wx_storm:     { country: "USA", region: "TN", dma: "635", zip: "38103" },
  wx_aqi:       { country: "USA", region: "NY", dma: "501", zip: "10312" },
  fw_spread:    { country: "USA", region: "CA", dma: "803", zip: "93101" },
  sp_goal:      { country: "USA", region: "NY", dma: "501", zip: "10036" },
  sp_golf:      { country: "USA", region: "FL", dma: "528", zip: "34108" },
  ws_wnba:      { country: "USA", region: "NY", dma: "501", zip: "10036" },
  ws_nwsl:      { country: "USA", region: "OR", dma: "820", zip: "97201" },
  fi_10k:       { country: "USA", region: "CA", dma: "803", zip: "94105" },
  fi_mna:       { country: "USA", region: "NY", dma: "501", zip: "10004" },
  gm_dota2:     { country: "USA", region: "WA", dma: "819", zip: "98101" },
  ent_starmeter:{ country: "USA", region: "NY", dma: "501", zip: "10036" },
};

const DEAL_IDS: Record<ExampleKey, string> = {
  wx_storm:     "RSQ-WX-STORM-ALERT",
  wx_aqi:       "RSQ-WX-AQI-HEALTH",
  fw_spread:    "RSQ-FW-PRED-SPREAD",
  sp_goal:      "FTB-LIVE-RED",
  sp_golf:      "GOLF-LIVE-LEADCHG",
  ws_wnba:      "WNBA-LIVE-CL5",
  ws_nwsl:      "NWSL-LIVE-GOAL",
  fi_10k:       "RSQ-FI-10K-AAPL",
  fi_mna:       "RSQ-FIN-MNA-ACTIVE",
  gm_dota2:     "RSQ-GM-E",
  ent_starmeter:"RSQ_STARmeter_Top100_7D_DMA_501",
};

const SIGNAL_IDS: Record<ExampleKey, string> = {
  wx_storm:     "weather.RSQ_SevereStorm_Alert",
  wx_aqi:       "weather.RSQ_AirQuality_Health",
  fw_spread:    "firewatch.RSQ_FW_PredictiveSpread",
  sp_goal:      "sports.ftb_red_card",
  sp_golf:      "sports.golf_leadchg",
  ws_wnba:      "sports.wnba_close_game",
  ws_nwsl:      "sports.nwsl_goal",
  fi_10k:       "finance.RSQ_FIN_10K",
  fi_mna:       "finance.RSQ_FIN_MNA_ACTIVE",
  gm_dota2:     "gaming.steam_ccu_zscore",
  ent_starmeter:"entertainment.RSQ_STARmeter",
};

const SCORES: Record<ExampleKey, number> = {
  wx_storm: 0.91, wx_aqi: 0.87, fw_spread: 0.96,
  sp_goal: 0.91, sp_golf: 0.84,
  ws_wnba: 0.89, ws_nwsl: 0.86,
  fi_10k: 0.79, fi_mna: 0.88,
  gm_dota2: 0.84, ent_starmeter: 0.82,
};

const TTL_REMAINING: Record<ExampleKey, number> = {
  wx_storm: 10800, wx_aqi: 7200, fw_spread: 3600,
  sp_goal: 2400, sp_golf: 1800,
  ws_wnba: 4500, ws_nwsl: 1500,
  fi_10k: 259200, fi_mna: 7200,
  gm_dota2: 86400, ent_starmeter: 604800,
};

const BID_FLOORS: Record<ExampleKey, number> = {
  wx_storm: 3.0, wx_aqi: 3.0, fw_spread: 3.0,
  sp_goal: 3.0, sp_golf: 3.0,
  ws_wnba: 3.0, ws_nwsl: 3.0,
  fi_10k: 6.0, fi_mna: 6.0,
  gm_dota2: 3.0, ent_starmeter: 3.0,
};

const BRAVIA_DEVICE = {
  ua: "Mozilla/5.0 (Linux; Android 12; BRAVIA 4K VH2; wv) AppleWebKit/537.36",
  ip: "150.252.163.243",
  devicetype: 3,
  os: "Android",
  ifa: "698349ae-9a72-43e9-95d6-0f148c5f2fde",
};

const FIRETV_DEVICE = {
  ua: "Mozilla/5.0 (Linux; Android Fire OS 11; AFTMM Build/PS7323.2875N)",
  ip: "72.229.28.185",
  devicetype: 3,
  os: "FireOS",
  ifa: "3076a6a4-b891-4f3d-9aaa-e2c7b5f1d832",
};

const ONCORE_APP = {
  id: "com.oncore.tv",
  name: "OnCore TV+",
  bundle: "com.oncore.tv",
  publisher: { id: "LeanbackDigital_RON_CTV_oRTB" },
};

const MOVIEZONE_APP = {
  id: "showmetelevision.com",
  name: "MovieZone",
  bundle: "showmetelevision.com",
  publisher: { id: "LeanbackDigital_SMT_CTV_oRTB" },
};

const APP_CONTEXTS: Record<ExampleKey, object> = {
  wx_storm: ONCORE_APP, wx_aqi: ONCORE_APP, fw_spread: ONCORE_APP,
  sp_goal: ONCORE_APP,  sp_golf: ONCORE_APP,
  ws_wnba: ONCORE_APP, ws_nwsl: ONCORE_APP,
  fi_10k: MOVIEZONE_APP, fi_mna: MOVIEZONE_APP,
  gm_dota2: ONCORE_APP, ent_starmeter: ONCORE_APP,
};

const DEVICE_CONTEXTS: Record<ExampleKey, object> = {
  wx_storm: BRAVIA_DEVICE, wx_aqi: BRAVIA_DEVICE, fw_spread: BRAVIA_DEVICE,
  sp_goal: BRAVIA_DEVICE,  sp_golf: BRAVIA_DEVICE,
  ws_wnba: BRAVIA_DEVICE, ws_nwsl: BRAVIA_DEVICE,
  fi_10k: FIRETV_DEVICE, fi_mna: FIRETV_DEVICE,
  gm_dota2: BRAVIA_DEVICE, ent_starmeter: BRAVIA_DEVICE,
};

const IMPRESSION_INVENTORY: Record<ExampleKey, string> = {
  wx_storm: "OnCore TV+ · Sony BRAVIA 4K",
  wx_aqi:   "OnCore TV+ · Sony BRAVIA 4K",
  fw_spread:"OnCore TV+ · Sony BRAVIA 4K",
  sp_goal:  "OnCore TV+ · Sony BRAVIA 4K",
  sp_golf:  "OnCore TV+ · Sony BRAVIA 4K",
  ws_wnba:  "OnCore TV+ · Sony BRAVIA 4K",
  ws_nwsl:  "OnCore TV+ · Sony BRAVIA 4K",
  fi_10k:   "MovieZone · Amazon Fire TV",
  fi_mna:   "MovieZone · Amazon Fire TV",
  gm_dota2: "OnCore TV+ · Sony BRAVIA 4K",
  ent_starmeter: "OnCore TV+ · Sony BRAVIA 4K",
};

const IFA_BY_EXAMPLE: Record<ExampleKey, string> = {
  wx_storm: "698349ae-9a72-43e9-95d6-0f148c5f2fde",
  wx_aqi:   "698349ae-9a72-43e9-95d6-0f148c5f2fde",
  fw_spread:"698349ae-9a72-43e9-95d6-0f148c5f2fde",
  sp_goal:  "698349ae-9a72-43e9-95d6-0f148c5f2fde",
  sp_golf:  "698349ae-9a72-43e9-95d6-0f148c5f2fde",
  ws_wnba:  "698349ae-9a72-43e9-95d6-0f148c5f2fde",
  ws_nwsl:  "698349ae-9a72-43e9-95d6-0f148c5f2fde",
  fi_10k:   "3076a6a4-b891-4f3d-9aaa-e2c7b5f1d832",
  fi_mna:   "3076a6a4-b891-4f3d-9aaa-e2c7b5f1d832",
  gm_dota2: "698349ae-9a72-43e9-95d6-0f148c5f2fde",
  ent_starmeter: "698349ae-9a72-43e9-95d6-0f148c5f2fde",
};

const CREATIVE_CUES: Partial<Record<ExampleKey, string>> = {
  wx_storm:  "Storm conditions approaching — prepare your facility now.",
  fw_spread: "Wildfire spread forecast active — route impacts imminent.",
  ws_wnba:   "Final minutes, one-possession game — the moment to show up.",
};

function buildBidBefore(example: ExampleKey) {
  return {
    id: "RYB8YAK1HXSJ",
    imp: [{
      id: "RYB8YAK1HXSJ",
      video: {
        mimes: ["video/mp4", "video/x-flv", "application/x-mpegURL"],
        minduration: 3,
        maxduration: 60,
        protocols: [2, 3, 5, 6],
        w: 1920,
        h: 1080,
        placement: 1,
        linearity: 1,
        skip: 0,
        api: [2],
      },
      bidfloor: BID_FLOORS[example],
      bidfloorcur: "USD",
      secure: 1,
      ssai: 3,
    }],
    app: APP_CONTEXTS[example],
    device: { ...DEVICE_CONTEXTS[example] as object, geo: BID_GEO[example] },
    ext: {},
  };
}

function buildBidAfter(example: ExampleKey) {
  const signal: Record<string, unknown> = {
    id: SIGNAL_IDS[example],
    score: SCORES[example],
    ttl_remaining_s: TTL_REMAINING[example],
  };
  if (CREATIVE_CUES[example]) signal.creative_cue = CREATIVE_CUES[example];

  return {
    id: "RYB8YAK1HXSJ",
    imp: [{
      id: "RYB8YAK1HXSJ",
      video: {
        mimes: ["video/mp4", "video/x-flv", "application/x-mpegURL"],
        minduration: 3,
        maxduration: 60,
        protocols: [2, 3, 5, 6],
        w: 1920,
        h: 1080,
        placement: 1,
        linearity: 1,
        skip: 0,
        api: [2],
      },
      bidfloor: BID_FLOORS[example],
      bidfloorcur: "USD",
      secure: 1,
      ssai: 3,
      pmp: {
        private_auction: 0,
        deals: [{ id: DEAL_IDS[example], bidfloor: BID_FLOORS[example], at: 1 }],
      },
      ext: {
        realsynq: { signals: [signal] },
      },
    }],
    app: APP_CONTEXTS[example],
    device: { ...DEVICE_CONTEXTS[example] as object, geo: BID_GEO[example] },
    ext: {},
  };
}

function buildImpression(example: ExampleKey) {
  return {
    event: "impression",
    tracker: "http://tracker-b-lnb.ortb.net/imp",
    params: {
      p: "3.030",
      pc: "${AUCTION_CURRENCY}",
    },
    source: "Limelight · LBD CTV (lnb)",
    inventory: IMPRESSION_INVENTORY[example],
    win_price_usd: 4.662,
    deal_id: DEAL_IDS[example],
    signal_id: SIGNAL_IDS[example],
    fired_at: "2026-05-19T14:22:09Z",
    ifa: IFA_BY_EXAMPLE[example],
  };
}

const SCHEMA_TABS: Array<{ id: SchemaTab; label: string }> = [
  { id: "signal",     label: "Signal Tag"      },
  { id: "before",     label: "Bid · Before"    },
  { id: "after",      label: "Bid · After"     },
  { id: "impression", label: "Impression"      },
];

const PRINCIPLES = [
  { icon: Shield,   title: "TTL governance",      desc: "Every signal carries explicit expiry. No stale activation. Buyer-side cache hints included." },
  { icon: Globe,    title: "Geo specificity",      desc: "Country, region, DMA, and zip-level granularity exposed where source data permits." },
  { icon: Hash,     title: "Stable IDs",           desc: "Deal IDs and audience KVs follow predictable naming so DSP-side mapping is one-shot." },
  { icon: Sparkles, title: "OpenRTB native",          desc: "Schema built for direct OpenRTB injection — deal IDs and audience KVs resolve deterministically at bid time." },
  { icon: Tag,      title: "Deal ID convention",   desc: "RSQ-{PKG}-{SIGNAL}-{GEO} — deterministic format. Map once in your DSP; all future signals resolve automatically." },
  { icon: Cpu,      title: "Creative governance",  desc: "TTL suppression and whitelist/safety logic baked into deal-level policy. No creative-level configuration required." },
];

export function MetadataView({ isMobile }: MetadataViewProps) {
  const [activeTab, setActiveTab] = useState<SchemaTab>("signal");
  const [activeExample, setActiveExample] = useState<ExampleKey>("wx_storm");
  const [copied, setCopied] = useState(false);

  const tabData =
    activeTab === "signal"     ? SIGNAL_TAGS[activeExample]
    : activeTab === "before"   ? buildBidBefore(activeExample)
    : activeTab === "after"    ? buildBidAfter(activeExample)
    : buildImpression(activeExample);

  const { filename } = EXAMPLES[activeExample];
  const tabFilename =
    activeTab === "signal"     ? filename
    : activeTab === "before"   ? `bid_request.before.${activeExample}.json`
    : activeTab === "after"    ? `bid_request.after.${activeExample}.json`
    : `impression.${activeExample}.json`;

  function handleCopy() {
    navigator.clipboard.writeText(JSON.stringify(tabData, null, 2)).catch(() => null);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  function handleExampleClick(key: ExampleKey) {
    setActiveExample(key);
    if (activeTab === "signal") return;
  }

  return (
    <div style={{ padding: isMobile ? "40px 16px 80px" : "60px 32px", maxWidth: 1200, margin: "0 auto" }}>
      <div className="mono" style={{ fontSize: 11, color: COLORS.cyan, letterSpacing: "0.15em", marginBottom: 12 }}>
        ◆ MACHINE-READABLE SIGNAL METADATA
      </div>
      <h2
        className="display"
        style={{
          fontSize: isMobile ? "clamp(32px, 9vw, 40px)" : 44,
          fontWeight: 700,
          margin: "0 0 16px",
          lineHeight: 1,
        }}
      >
        Standards-aligned. OpenRTB-native.
      </h2>
      <p
        style={{
          color: COLORS.inkDim,
          fontSize: isMobile ? 14 : 16,
          maxWidth: 700,
          lineHeight: 1.6,
          marginBottom: isMobile ? 32 : 48,
        }}
      >
        Every signal carries explicit governance metadata — provenance, TTL, geo specificity,
        intent score, and activation surface. The viewer below traces the full enrichment
        lifecycle from raw signal tag through OpenRTB bid request injection and impression
        tracking, injected natively into the OpenRTB bidstream at request time.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1.4fr 1fr",
          gap: isMobile ? 16 : 24,
        }}
      >
        {/* Schema viewer panel */}
        <div
          style={{
            background: "rgba(10, 19, 32, 0.85)",
            backdropFilter: "blur(12px)",
            border: `1px solid ${COLORS.border}`,
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          {/* Grouped example selector */}
          <div
            style={{
              padding: "12px 16px",
              borderBottom: `1px solid ${COLORS.border}`,
              background: "rgba(15, 26, 44, 0.6)",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {EXAMPLE_GROUPS.map((group) => (
              <div key={group.label} style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span
                  className="mono"
                  style={{
                    fontSize: 9,
                    letterSpacing: "0.14em",
                    color: group.color,
                    opacity: 0.7,
                    minWidth: 46,
                    flexShrink: 0,
                  }}
                >
                  {group.label}
                </span>
                {group.keys.map((key) => {
                  const sel = activeExample === key;
                  return (
                    <button
                      key={key}
                      onClick={() => handleExampleClick(key)}
                      className="mono"
                      style={{
                        fontSize: 10,
                        padding: "3px 9px",
                        borderRadius: 4,
                        border: `1px solid ${sel ? group.color : COLORS.border}`,
                        background: sel ? `${group.color}18` : "transparent",
                        color: sel ? group.color : COLORS.inkMute,
                        letterSpacing: "0.05em",
                        cursor: "pointer",
                        transition: "all 0.12s",
                      }}
                    >
                      {EXAMPLES[key].label}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Tab bar */}
          <div
            style={{
              display: "flex",
              borderBottom: `1px solid ${COLORS.border}`,
              background: "rgba(15, 26, 44, 0.8)",
            }}
          >
            {SCHEMA_TABS.map(({ id, label }) => {
              const active = activeTab === id;
              return (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className="mono"
                  style={{
                    flex: 1,
                    padding: "10px 4px",
                    fontSize: isMobile ? 9 : 10,
                    letterSpacing: "0.04em",
                    border: "none",
                    borderBottom: `2px solid ${active ? COLORS.cyan : "transparent"}`,
                    background: "transparent",
                    color: active ? COLORS.cyan : COLORS.inkMute,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Filename bar with copy button */}
          <div
            style={{
              padding: "8px 14px",
              borderBottom: `1px solid ${COLORS.border}`,
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "rgba(15, 26, 44, 0.5)",
            }}
          >
            <div style={{ display: "flex", gap: 5 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#fb7185" }} />
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#fbbf24" }} />
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#a3e635" }} />
            </div>
            <div
              className="mono"
              style={{
                fontSize: 11,
                color: COLORS.inkDim,
                marginLeft: 6,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {tabFilename}
            </div>
            <button
              onClick={handleCopy}
              title="Copy JSON"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "3px 8px",
                borderRadius: 4,
                border: `1px solid ${copied ? COLORS.lime : COLORS.border}`,
                background: copied ? `${COLORS.lime}14` : "transparent",
                color: copied ? COLORS.lime : COLORS.inkMute,
                cursor: "pointer",
                fontSize: 10,
                transition: "all 0.15s",
                flexShrink: 0,
              }}
            >
              {copied
                ? <><Check size={12} /><span className="mono" style={{ letterSpacing: "0.04em" }}>copied</span></>
                : <><Copy size={12} /><span className="mono" style={{ letterSpacing: "0.04em" }}>copy</span></>
              }
            </button>
          </div>

          <pre
            className="mono no-scrollbar"
            style={{
              margin: 0,
              padding: isMobile ? 16 : 20,
              fontSize: isMobile ? 11 : 12,
              lineHeight: 1.7,
              color: COLORS.ink,
              overflow: "auto",
              maxHeight: isMobile ? 360 : 480,
            }}
          >
            <code dangerouslySetInnerHTML={{ __html: syntaxHighlight(JSON.stringify(tabData, null, 2)) }} />
          </pre>
        </div>

        {/* Governance principles */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {PRINCIPLES.map((b, i) => (
            <div
              key={i}
              style={{
                background: "rgba(15, 26, 44, 0.8)",
                backdropFilter: "blur(10px)",
                border: `1px solid ${COLORS.border}`,
                borderRadius: 8,
                padding: 18,
              }}
            >
              <b.icon size={16} color={COLORS.cyan} style={{ marginBottom: 10 }} />
              <div className="display" style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>
                {b.title}
              </div>
              <p style={{ fontSize: 13, color: COLORS.inkDim, margin: 0, lineHeight: 1.5 }}>
                {b.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * syntaxHighlight — escapes HTML and colorizes a JSON string for display.
 * Output is safe for dangerouslySetInnerHTML: input is always from JSON.stringify
 * and we escape <, >, & first.
 */
function syntaxHighlight(json: string): string {
  const escaped = json.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return escaped.replace(
    /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
    (match) => {
      let cls: string = COLORS.amber;
      if (/^"/.test(match)) {
        cls = /:$/.test(match) ? COLORS.cyan : COLORS.lime;
      } else if (/true|false/.test(match)) {
        cls = COLORS.violet;
      } else if (/null/.test(match)) {
        cls = COLORS.rose;
      }
      return `<span style="color:${cls}">${match}</span>`;
    }
  );
}
